import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../assets/css/Instruction/Instruction.css";
import axios from "axios";
import { BASE_URL } from "../../store/config";

/* ─── inject global keyframes once ─────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("instr-styles")) return;
  const el = document.createElement("style");
  el.id = "instr-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(22px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes spinAnim { to { transform: rotate(360deg); } }
    @keyframes pulseRing {
      0%   { box-shadow: 0 0 0 0 rgba(99,102,241,0.40); }
      70%  { box-shadow: 0 0 0 14px rgba(99,102,241,0); }
      100% { box-shadow: 0 0 0 0  rgba(99,102,241,0); }
    }
    @keyframes shimmer {
      0%   { background-position: -600px 0; }
      100% { background-position:  600px 0; }
    }

    .i-fadeup { animation: fadeUp 0.5s cubic-bezier(.22,1,.36,1) both; }
    .i-d1 { animation-delay: 0.05s; }
    .i-d2 { animation-delay: 0.13s; }
    .i-d3 { animation-delay: 0.21s; }
    .i-d4 { animation-delay: 0.29s; }
    .i-d5 { animation-delay: 0.37s; }

    .i-spinner {
      width: 40px; height: 40px; border-radius: 50%;
      border: 4px solid #eef0ff; border-top-color: #6366f1;
      animation: spinAnim 0.75s linear infinite;
    }
    .i-btn-spinner {
      width: 18px; height: 18px; border-radius: 50%;
      border: 2px solid rgba(255,255,255,0.35); border-top-color: #fff;
      animation: spinAnim 0.7s linear infinite;
    }

    .i-start-btn {
      display: inline-flex; align-items: center; justify-content: center; gap: 10px;
      width: 100%; max-width: 400px;
      padding: 16px 48px; font-size: 16px; font-weight: 700;
      font-family: 'Plus Jakarta Sans', sans-serif;
      color: #fff; border: none; border-radius: 999px; cursor: pointer;
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
      box-shadow: 0 8px 24px rgba(99,102,241,0.35);
      transition: box-shadow 0.2s, transform 0.15s, opacity 0.15s;
      animation: pulseRing 2.8s ease-out infinite;
    }
    .i-start-btn:hover:not(:disabled) {
      box-shadow: 0 14px 40px rgba(99,102,241,0.48);
      transform: translateY(-2px);
    }
    .i-start-btn:active:not(:disabled) { transform: scale(0.98); }
    .i-start-btn:disabled { opacity: 0.75; cursor: not-allowed; animation: none; }
    .i-btn-arrow { display: inline-block; transition: transform 0.15s ease; }
    .i-start-btn:hover .i-btn-arrow { transform: translateX(5px); }

    .i-stat-card {
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    .i-stat-card:hover {
      transform: translateY(-5px);
      box-shadow: 0 14px 36px rgba(0,0,0,0.10) !important;
    }

    /* API HTML instructions styling */
    .i-html ul { list-style: none; padding: 0; display: flex; flex-direction: column; gap: 10px; }
    .i-html li {
      display: flex; align-items: flex-start; gap: 12px;
      padding: 14px 18px; border-radius: 12px;
      background: #f8f9ff; border: 1px solid #eef0ff;
      font-size: 15px; color: #374151; line-height: 1.6;
      transition: background 0.2s, border-color 0.2s;
    }
    .i-html li:hover { background: #eef0ff; border-color: #c7d2fe; }
    .i-html li::before {
      content: '';
      flex-shrink: 0; margin-top: 2px;
      width: 20px; height: 20px; border-radius: 50%;
      background: #6366f1 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='white'%3E%3Cpath fill-rule='evenodd' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' clip-rule='evenodd'/%3E%3C/svg%3E") center/12px no-repeat;
    }

    .i-step-connector { flex: 1; height: 2px; background: #e5e7eb; border-radius: 2px; position: relative; overflow: hidden; }
    .i-step-connector::after {
      content: ''; position: absolute; inset: 0;
      background: #6366f1;
      transform: scaleX(0); transform-origin: left;
      transition: transform 0.6s ease 0.3s;
    }
    .i-step-connector.active::after { transform: scaleX(1); }

    @media (max-width: 640px) {
      .i-stat-grid { grid-template-columns: 1fr !important; }
      .i-hero-title { font-size: 24px !important; }
      .i-card { padding: 20px !important; }
      .i-main { padding: 24px 16px 70px !important; }
      .i-header-inner { padding: 0 16px !important; }
    }
    @media (min-width: 641px) and (max-width: 900px) {
      .i-stat-grid { grid-template-columns: repeat(3, 1fr) !important; }
    }
  `;
  document.head.appendChild(el);
};

/* ─── colour palette ────────────────────────────────────────────────────────── */
const C = {
  bg: "#f5f6ff",
  white: "#ffffff",
  primary: "#6366f1",
  primaryDk: "#4f46e5",
  primaryLt: "#eef0ff",
  text: "#111827",
  muted: "#6b7280",
  border: "#e5e7eb",
  amber: "#f59e0b",
  amberBg: "#fffbeb",
  amberBorder: "#fde68a",
  green: "#10b981",
  blue: "#3b82f6",
  purple: "#8b5cf6",
};

/* ─── StatCard ─────────────────────────────────────────────────────────────── */
const StatCard = ({ icon, label, value, accent }) => (
  <div
    className="i-stat-card"
    style={{
      background: C.white,
      border: `1px solid ${C.border}`,
      borderRadius: 16,
      padding: "20px 22px",
      display: "flex",
      alignItems: "center",
      gap: 16,
      boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    }}
  >
    <div
      style={{
        width: 50, height: 50, borderRadius: 14, flexShrink: 0,
        background: `${accent}1a`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 22,
      }}
    >
      {icon}
    </div>
    <div>
      <p style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>
        {label}
      </p>
      <p style={{ fontSize: 21, fontWeight: 800, color: C.text }}>{value}</p>
    </div>
  </div>
);

/* ─── Spinner screen ─────────────────────────────────────────────────────────── */
const LoadingScreen = () => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: C.bg, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
    <div className="i-spinner" />
    <p style={{ marginTop: 16, color: C.muted, fontSize: 14, fontWeight: 500 }}>Loading assessment…</p>
  </div>
);

/* ─── Error screen ─────────────────────────────────────────────────────────── */
const ErrorScreen = () => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: C.bg, fontFamily: "'Plus Jakarta Sans', sans-serif", textAlign: "center", padding: "0 24px" }}>
    <div style={{ fontSize: 52, marginBottom: 16 }}>⚠️</div>
    <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 8 }}>Assessment not found</h2>
    <p style={{ color: C.muted, fontSize: 15 }}>This assessment doesn't exist or has been removed.</p>
  </div>
);

/* ─── Main component ───────────────────────────────────────────────────────── */
function Instruction() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {

  const fetchAssessment = async () => {

    try {

      const token =
        localStorage.getItem("access_token") ||
        localStorage.getItem("accessToken");

      if (!token) {
        navigate("/login");
        return;
      }

      const res = await axios.get(
        `${BASE_URL}api/assessments/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setData(res.data);

    } catch (err) {

      console.error(
        "Error fetching assessment:",
        err.response?.data || err.message
      );

      // optional redirect on unauthorized
      if (err.response?.status === 401) {
        localStorage.clear();
        navigate("/login");
      }

    } finally {

      setLoading(false);

    }
  };

  fetchAssessment();

}, [id, navigate]);

  const handleStart = async () => {
    setStarting(true);
    try { await document.documentElement.requestFullscreen(); } catch (_) {}
    setTimeout(() => navigate(`/assessment/${id}`, { state: { started: true } }), 350);
  };

  if (loading) return <LoadingScreen />;
  if (!data) return <ErrorScreen />;

  const totalMarks = data.questions?.reduce((s, q) => s + (q.marks || 0), 0) || data.total_score || 0;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.text }}>

      {/* ── Header ── */}
      {/* Header */}
      <div className="header">
        <img
          src="/assets/images/image.png"
          alt="Logo"
          className="logo"
        />
        
      </div>

      {/* ── Step indicator ── */}
      <div style={{ background: C.white, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 820, margin: "0 auto", padding: "14px 24px", display: "flex", alignItems: "center", gap: 10 }}>
          {/* Step 1 — active */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.primary, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>1</div>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.primary }}>Instructions</span>
          </div>
          {/* Connector */}
          <div className="i-step-connector active" />
          {/* Step 2 */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#e5e7eb", color: C.muted, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>2</div>
            <span style={{ fontSize: 14, fontWeight: 500, color: C.muted }}>Assessment</span>
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <main className="i-main" style={{ maxWidth: 820, margin: "0 auto", padding: "36px 24px 80px" }}>

        {/* Hero */}
        <div className="i-fadeup i-d1" style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: C.primaryLt, border: `1px solid #c7d2fe`, borderRadius: 999, padding: "5px 16px", marginBottom: 16 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.primary, display: "inline-block" }} />
            <span style={{ fontSize: 12, color: C.primary, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Ready to begin</span>
          </div>
          <h1
            className="i-hero-title"
            style={{ fontSize: 32, fontWeight: 800, color: C.text, lineHeight: 1.2, marginBottom: 10 }}
          >
            {data.title}
          </h1>
          {data.description && (
            <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.65, maxWidth: 480, margin: "0 auto" }}>
              {data.description}
            </p>
          )}
        </div>

        {/* Stat cards */}
        <div
          className="i-fadeup i-d2 i-stat-grid"
          style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 28 }}
        >
          <StatCard icon="⏱️" label="Duration" value={`${data.duration} min`} accent={C.blue} />
          <StatCard icon="📋" label="Questions" value={data.questions?.length ?? 0} accent={C.purple} />
          <StatCard icon="🎯" label="Pass Mark" value={`${data.passing_score} / ${totalMarks}`} accent={C.green} />
        </div>

        {/* Instructions card */}
        <div
          className="i-fadeup i-d3 i-card"
          style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 20, padding: 28, marginBottom: 18, boxShadow: "0 4px 18px rgba(0,0,0,0.04)" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
            <div style={{ width: 38, height: 38, borderRadius: 11, background: C.primaryLt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>📝</div>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: C.text }}>Instructions</h2>
          </div>

          {data.instructions ? (
            <div className="i-html" dangerouslySetInnerHTML={{ __html: data.instructions }} />
          ) : (
            <div className="i-html">
              <ul>
                {[
                  "Read each question carefully before selecting your answer",
                  "Navigate between questions using the Previous and Next buttons",
                  "All questions must be answered before submitting",
                  "Your progress is automatically saved as you go",
                  "Review all answers before final submission",
                ].map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            </div>
          )}
        </div>

        {/* Rules card */}
        <div
          className="i-fadeup i-d4"
          style={{ background: C.amberBg, border: `1px solid ${C.amberBorder}`, borderRadius: 20, padding: "22px 24px", marginBottom: 36 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#92400e" }}>Important Rules</h3>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              ["🚫", "Do not switch tabs — assessment will auto-submit"],
              ["🖥️", "Fullscreen mode is required throughout"],
              ["⏰", "Timer cannot be paused once started"],
            ].map(([icon, text], i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#78350f", fontWeight: 500 }}>
                <span style={{ fontSize: 16 }}>{icon}</span>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="i-fadeup i-d5" style={{ textAlign: "center" }}>
          <button
            className="i-start-btn"
            onClick={handleStart}
            disabled={starting}
          >
            {starting ? (
              <>
                <div className="i-btn-spinner" />
                Starting…
              </>
            ) : (
              <>
                Begin Assessment
                <span className="i-btn-arrow">→</span>
              </>
            )}
          </button>
          <p style={{ marginTop: 14, fontSize: 13, color: C.muted }}>
            Make sure you're ready — the timer starts immediately.
          </p>
        </div>

      </main>
    </div>
  );
}

export default Instruction;
